import React, { memo, useCallback, useMemo, useState } from 'react';
import dayjs from 'utils/dayjs';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

// import Icon from 'components/commons/Icon';
import CurrencyYearArchiveMonthAccordion from 'components/CurrencyYearArchiveMonthAccordion';
import CurrencyYearArchiveNbuRateValue from 'components/CurrencyYearArchiveNbuRateValue';
import CurrencyYearArchiveRatesTableDays from 'components/CurrencyYearArchiveRatesTableDays';
import {
  RateCell,
  RateValue,
} from 'components/CurrencyYearArchiveRatesTableDays/styles';
import CurrencyYearArchiveRatesTableHead from 'components/CurrencyYearArchiveRatesTableHead';
import { Table } from 'components/CurrencyYearArchiveRatesTableHead/styles';
import CurrencyYearArchiveRatesTablePlaceholder from 'components/CurrencyYearArchiveRatesTablePlaceholder';
import { CURRENCY_YEAR_ARCHIVE_SOURCE } from 'constants/currencyYearArchive';
import { SORT_ORDER } from 'constants/utils';
import {
  formatCurrencyYearArchiveCashRate,
  formatCurrencyYearArchiveNbuRate,
  getCurrencyYearArchiveDefaultExpandedMonth,
  getCurrencyYearArchiveSourceSectionId,
  getNextCurrencyYearArchiveSort,
  isCurrentArchiveYear,
  sortCurrencyYearArchiveDays,
} from 'utils/currencyYearArchive';

import {
  // DownloadButton,
  Empty,
  Header,
  Source,
  TableScrollWrapper,
  Title,
  Wrapper,
  YearEndLabel,
  YearEndRow,
  YearEndValues,
} from './styles';

const DEFAULT_SORT = {
  key: 'date',
  order: SORT_ORDER.asc,
};

const CurrencyYearArchiveSourceSection = ({
  optionType,
  source,
  year,
  currencyLabel,
  highlightedDate,
  isArchiveLoading,
}) => {
  const { formatMessage, formatNumber, locale } = useIntl();
  const [sort, setSort] = useState(DEFAULT_SORT);
  const isNbu = optionType === CURRENCY_YEAR_ARCHIVE_SOURCE.nbu;
  const isCards = optionType === CURRENCY_YEAR_ARCHIVE_SOURCE.cards;
  const columnCount = isNbu ? 2 : 3;

  const formatRate = useCallback(
    value =>
      isNbu
        ? formatCurrencyYearArchiveNbuRate(value, formatNumber)
        : formatCurrencyYearArchiveCashRate(value, formatNumber),
    [formatNumber, isNbu]
  );

  const formatDayLabel = useCallback(
    isoDate =>
      dayjs(isoDate, 'YYYY-MM-DD', true)
        .locale(locale)
        .format('DD.MM.YYYY'),
    [locale]
  );

  const renderRateCells = useCallback(
    day =>
      isNbu ? (
        <RateCell>
          <CurrencyYearArchiveNbuRateValue
            value={day.rate}
            formatNumber={formatNumber}
          />
        </RateCell>
      ) : (
        <>
          <RateCell>
            <RateValue>{formatRate(day.buy)}</RateValue>
          </RateCell>
          <RateCell>
            <RateValue>{formatRate(day.sell)}</RateValue>
          </RateCell>
        </>
      ),
    [formatNumber, formatRate, isNbu]
  );

  const handleSort = useCallback(columnKey => {
    setSort(currentSort =>
      getNextCurrencyYearArchiveSort(currentSort, columnKey)
    );
  }, []);

  const months = useMemo(() => source?.months || [], [source?.months]);
  const { yearEnd } = source || {};
  const hasData = Boolean(source?.hasData && months.length);

  const defaultExpandedMonth = useMemo(
    () =>
      getCurrencyYearArchiveDefaultExpandedMonth({
        year,
        months,
        highlightedDate,
      }),
    [highlightedDate, months, year]
  );

  const tableColumns = useMemo(() => {
    const columns = [
      {
        key: 'date',
        label: formatMessage({ id: 'mbArchivePage.tradeHistory.date' }),
      },
    ];

    if (isNbu) {
      columns.push({
        key: 'rate',
        label: formatMessage({ id: 'table.course.title' }),
      });
    } else {
      columns.push(
        {
          key: 'buy',
          label: formatMessage({ id: 'table.purchase.title' }),
        },
        {
          key: 'sell',
          label: formatMessage({ id: 'table.sale.title' }),
        }
      );
    }

    return columns;
  }, [formatMessage, isNbu]);

  const yearEndValues = useMemo(() => {
    if (!yearEnd) {
      return null;
    }

    if (isNbu) {
      return formatRate(yearEnd.rate);
    }

    return `${formatRate(yearEnd.buy)} / ${formatRate(yearEnd.sell)}`;
  }, [formatRate, isNbu, yearEnd]);

  const yearEndLabel = useMemo(() => {
    if (!yearEnd) {
      return null;
    }

    if (isCurrentArchiveYear(year) && yearEnd.asOfDate) {
      const formattedDate = dayjs(yearEnd.asOfDate, 'YYYY-MM-DD', true)
        .locale(locale)
        .format('DD.MM.YYYY');

      return formatMessage(
        { id: `currencyYearArchivePage.yearEndAsOf.${optionType}` },
        { currency: currencyLabel, date: formattedDate }
      );
    }

    return formatMessage(
      { id: `currencyYearArchivePage.yearEnd.${optionType}` },
      { currency: currencyLabel, year }
    );
  }, [currencyLabel, formatMessage, locale, optionType, year, yearEnd]);

  const footerSourceId = useMemo(() => {
    if (optionType === CURRENCY_YEAR_ARCHIVE_SOURCE.nbu) {
      return 'table.source.desc';
    }

    if (
      optionType === CURRENCY_YEAR_ARCHIVE_SOURCE.banks ||
      (isCards && hasData)
    ) {
      return 'table.source.banks.desc';
    }

    return 'table.source.mb.desc';
  }, [hasData, isCards, optionType]);

  return (
    <Wrapper id={getCurrencyYearArchiveSourceSectionId(optionType)}>
      <Header>
        <Title>
          <FormattedMessage
            id={`currencyYearArchivePage.source.${optionType}`}
          />
        </Title>
        {/* TODO: CUDEV-4132 — показати після готовності backend download API
        {hasData ? (
          <DownloadButton type="button">
            <Icon name="downloadArchiveIcon" />
            <FormattedMessage id="currencyYearArchivePage.download" />
          </DownloadButton>
        ) : null}
        */}
      </Header>

      {hasData && (
        <TableScrollWrapper>
          <Table>
            <colgroup>
              {tableColumns.map(column => (
                <col
                  key={column.key}
                  style={{ width: `${100 / tableColumns.length}%` }}
                />
              ))}
            </colgroup>
            <CurrencyYearArchiveRatesTableHead
              columns={tableColumns}
              sort={sort}
              onSort={handleSort}
            />

            {months.map(month => {
              const summary = isNbu
                ? formatMessage(
                    {
                      id: `currencyYearArchivePage.monthAverage.${optionType}`,
                    },
                    { rate: formatRate(month.averageRate) }
                  )
                : formatMessage(
                    {
                      id: `currencyYearArchivePage.monthAverage.${optionType}`,
                    },
                    {
                      buy: formatRate(month.averageBuy),
                      sell: formatRate(month.averageSell),
                    }
                  );

              return (
                <CurrencyYearArchiveMonthAccordion
                  key={`${year}-${month.month}`}
                  month={month.month}
                  year={year}
                  summary={summary}
                  highlightedDate={highlightedDate}
                  defaultExpanded={
                    Number(defaultExpandedMonth) === Number(month.month)
                  }
                  columnCount={columnCount}
                >
                  <CurrencyYearArchiveRatesTableDays
                    days={sortCurrencyYearArchiveDays(month.days, sort)}
                    formatDayLabel={formatDayLabel}
                    renderRateCells={renderRateCells}
                    highlightedDate={highlightedDate}
                  />
                </CurrencyYearArchiveMonthAccordion>
              );
            })}
          </Table>

          {yearEnd ? (
            <YearEndRow>
              <YearEndLabel>{yearEndLabel}</YearEndLabel>
              <YearEndValues>{yearEndValues}</YearEndValues>
            </YearEndRow>
          ) : null}
        </TableScrollWrapper>
      )}
      {!hasData && isArchiveLoading && (
        <TableScrollWrapper>
          <Table>
            <colgroup>
              {tableColumns.map(column => (
                <col
                  key={column.key}
                  style={{ width: `${100 / tableColumns.length}%` }}
                />
              ))}
            </colgroup>
            <CurrencyYearArchiveRatesTablePlaceholder
              columnCount={columnCount}
            />
          </Table>
        </TableScrollWrapper>
      )}
      {!hasData && !isArchiveLoading && (
        <Empty>
          <FormattedMessage id="empty" />
        </Empty>
      )}

      <Source>
        <FormattedMessage id={footerSourceId} />
      </Source>
    </Wrapper>
  );
};

CurrencyYearArchiveSourceSection.propTypes = {
  optionType: PropTypes.oneOf(Object.values(CURRENCY_YEAR_ARCHIVE_SOURCE))
    .isRequired,
  source: PropTypes.shape({
    hasData: PropTypes.bool,
    months: PropTypes.arrayOf(PropTypes.object),
    yearEnd: PropTypes.object,
  }),
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  currencyLabel: PropTypes.string.isRequired,
  highlightedDate: PropTypes.string,
  isArchiveLoading: PropTypes.bool,
};

CurrencyYearArchiveSourceSection.defaultProps = {
  source: null,
  highlightedDate: null,
  isArchiveLoading: false,
};

export default memo(CurrencyYearArchiveSourceSection);
